import React, {useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {SaveButton} from "../../../../components/button/SaveButton";
import {t} from "../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {CancelButton} from "../../../../components/button/CancelButton";
import DialogActions from "../../../../components/dialogs/DialogActions";
import TextField from "../../../../components/fields/textfield/TextField";
import DialogContent from "../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../components/dialogs/DialogTitle";
import Dialog from "../../../../components/dialogs/Dialog";
import Grid from "../../../../components/dialogs/Grid";
import Snackbar from "../snackbar/Snackbar";
import {UserGroupDto} from "../../../../common/apis/usergroup/UserGroupDto";
import {UserGroupUpdateDto} from "../../../../common/apis/usergroup/UserGroupUpdateDto";
import userGroupApiClient from "../../../../common/apis/usergroup/UserGroupApiClient";
import {UserGroupsTranslationKey} from "./UserGroupsTranslationKey";
import {invalidateFindAllUserGroupsQueryCache, invalidateFindUserGroupByIdQueryCache} from "./UserGroupQuery";
import {ValidationError} from "../../../../common/ValidationError";
import {UserGroupErrorCode} from "./UserGroupErrorCode";

interface UserGroupEditDialogProps {
    userGroup: UserGroupDto;
    onClose: () => void;
    onGroupUpdated: () => void;
}

export default function UserGroupEditDialog(props: Readonly<UserGroupEditDialogProps>) {
    const {userGroup, onClose, onGroupUpdated} = props;
    const queryClient = useQueryClient();

    const [name, setName] = useState<string>(userGroup.name);
    const [description, setDescription] = useState<string>(userGroup.description ?? "");
    const [nameError, setNameError] = useState<string | undefined>();

    function validateName(): boolean {
        if (name.trim().length === 0) {
            setNameError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
        setNameError(undefined);
        return true;
    }

    function saveChanges() {
        if (validateName()) {
            const updateDto: UserGroupUpdateDto = {
                name: name.trim(),
                description: description.trim() || undefined,
            };
            userGroupApiClient.update(userGroup.id, updateDto)
                .then(() => {
                    invalidateFindAllUserGroupsQueryCache(queryClient);
                    invalidateFindUserGroupByIdQueryCache(userGroup.id, queryClient);
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                    onGroupUpdated();
                    onClose();
                })
                .catch((err) => {
                    if (err instanceof ValidationError && err.error.code === UserGroupErrorCode.NAME_ALREADY_EXISTS) {
                        setNameError(t(UserGroupsTranslationKey.USER_GROUP_NAME_ALREADY_EXISTS));
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
                    }
                });
        }
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle title={t(UserGroupsTranslationKey.EDIT_USER_GROUP)}
                         onDialogClosed={onClose}
            />
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <TextField id="user-group-edit-name-field"
                                   label={t(UserGroupsTranslationKey.NAME)}
                                   value={name}
                                   errorMessage={nameError}
                                   onChange={(newName) => setName(newName)}
                                   required={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="user-group-edit-description-field"
                                   label={t(UserGroupsTranslationKey.DESCRIPTION)}
                                   value={description}
                                   onChange={(newDesc) => setDescription(newDesc)}
                                   multiline={true}
                                   rows={4}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}/>
                <SaveButton onClick={saveChanges}/>
            </DialogActions>
        </Dialog>
    );
}
